import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import FileAPI from 'services/api/file-updated.api';
import { BulkUpdateResponse, FileAction } from 'interface/file/file.interface';
import { FILE_STATUS } from 'pages/ExperienceCanvas/types';
import { showSnackbar } from 'store/snackbar/snackbar.action';
import { getFileActionErrorSnackbarContent } from './fileStatusGrouping';

type ActionLabels = {
  successSingular: string;
  successPlural?: string;
};

const DEFAULT_LABELS: Record<FileAction, ActionLabels> = {
  PROCESS: { successSingular: 'queued for processing' },
  PAUSE: { successSingular: 'paused' },
  RESUME: { successSingular: 'resumed' },
};

type RunByIdsArgs = {
  action: FileAction;
  fileIds: string[];
  optimisticStatus: FILE_STATUS;
};

function useFileActionRunnerState() {
  const fileAPI = useMemo(() => new FileAPI(), []);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [localFileStatuses, setLocalFileStatuses] = useState<Record<string, string>>({});
  const [localFileErrors, setLocalFileErrors] = useState<Record<string, string>>({});
  const [isRunning, setIsRunning] = useState(false);
  const [inFlightFileIds, setInFlightFileIds] = useState<Set<string>>(new Set());
  const isRunningRef = useRef(false);

  useEffect(() => {
    if (!isRunning) {
      isRunningRef.current = false;
    }
  }, [isRunning]);

  const applyOptimistic = useCallback((ids: string[], status: FILE_STATUS) => {
    setLocalFileStatuses((prev) => ({
      ...prev,
      ...Object.fromEntries(ids.map((id) => [id, status])),
    }));
    setLocalFileErrors((prev) => {
      const next = { ...prev };
      ids.forEach((id) => delete next[id]);
      return next;
    });
  }, []);

  const reconcile = useCallback((response: BulkUpdateResponse, optimisticIds: string[]) => {
    const erroredIds = new Set<string>();
    const errorMessages: Record<string, string> = {};
    response.results.forEach((r) => {
      if (r.status === 'ERROR') {
        erroredIds.add(r.fileId);
        errorMessages[r.fileId] = r.statusMessage || 'Action failed.';
      }
    });

    setLocalFileStatuses((prev) => {
      const next = { ...prev };
      optimisticIds.forEach((id) => {
        if (erroredIds.has(id)) delete next[id];
      });
      return next;
    });

    if (Object.keys(errorMessages).length > 0) {
      setLocalFileErrors((prev) => ({ ...prev, ...errorMessages }));
    }

    if (erroredIds.size > 0) {
      setInFlightFileIds((prev) => {
        const next = new Set(prev);
        erroredIds.forEach((id) => next.delete(id));
        return next;
      });
    }
  }, []);

  const announce = useCallback(
    (response: BulkUpdateResponse) => {
      const { action, status, summary } = response;
      const labels = DEFAULT_LABELS[action];
      const verb = labels.successPlural ?? labels.successSingular;
      const fileWord = (n: number) => `${n} file${n !== 1 ? 's' : ''}`;

      if (status === 'ALL_SUCCESS') {
        dispatch(
          showSnackbar({
            content:
              action === 'PROCESS' ? 'Your files will be processed shortly' : `${fileWord(summary.succeeded)} ${verb}.`,
            type: 'success',
          }),
        );
      } else if (status === 'PARTIAL_SUCCESS') {
        dispatch(
          showSnackbar({
            content: `${fileWord(summary.succeeded)} ${verb}. ${summary.failed} failed.`,
            type: 'warning',
          }),
        );
      } else {
        dispatch(
          showSnackbar({
            content: `Failed to ${action.toLowerCase()} ${fileWord(summary.failed)}. Please try again.`,
            type: 'error',
          }),
        );
      }
    },
    [dispatch],
  );

  const finalize = useCallback(
    async (optimisticIds: string[]) => {
      await queryClient.invalidateQueries(['ctx-data-file']);
      setLocalFileStatuses((prev) => {
        const next = { ...prev };
        optimisticIds.forEach((id) => delete next[id]);
        return next;
      });
    },
    [queryClient],
  );

  const runByIds = useCallback(
    async ({ action, fileIds, optimisticStatus }: RunByIdsArgs) => {
      if (fileIds.length === 0) return null;
      if (isRunningRef.current) return null;
      isRunningRef.current = true;
      setIsRunning(true);
      setInFlightFileIds((prev) => {
        const next = new Set(prev);
        if (action === 'PROCESS' || action === 'RESUME') {
          fileIds.forEach((id) => next.add(id));
        } else if (action === 'PAUSE') {
          fileIds.forEach((id) => next.delete(id));
        }
        return next;
      });
      applyOptimistic(fileIds, optimisticStatus);
      try {
        const response = await fileAPI.applyFileAction(action, fileIds);
        reconcile(response, fileIds);
        announce(response);
        await finalize(fileIds);
        return response;
      } catch (err) {
        setLocalFileStatuses((prev) => {
          const next = { ...prev };
          fileIds.forEach((id) => delete next[id]);
          return next;
        });
        setInFlightFileIds((prev) => {
          const next = new Set(prev);
          fileIds.forEach((id) => next.delete(id));
          return next;
        });
        dispatch(
          showSnackbar({
            content: getFileActionErrorSnackbarContent(action, err),
            type: 'error',
          }),
        );
        await queryClient.invalidateQueries(['ctx-data-file']);
        return null;
      } finally {
        setIsRunning(false);
      }
    },
    [applyOptimistic, reconcile, announce, finalize, fileAPI, dispatch, queryClient],
  );

  return {
    localFileStatuses,
    localFileErrors,
    isRunning,
    inFlightFileIds,
    setInFlightFileIds,
    runByIds,
    setLocalFileStatuses,
  };
}

type FileActionRunnerValue = ReturnType<typeof useFileActionRunnerState>;

const FileActionRunnerContext = createContext<FileActionRunnerValue | null>(null);

export const FileActionRunnerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const value = useFileActionRunnerState();
  return <FileActionRunnerContext.Provider value={value}>{children}</FileActionRunnerContext.Provider>;
};

export function useFileActionRunner(): FileActionRunnerValue {
  const context = useContext(FileActionRunnerContext);
  if (!context) {
    throw new Error('useFileActionRunner must be used within a FileActionRunnerProvider');
  }
  return context;
}
